import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import useSWR from "swr";
import urls from "../../api/urls"
import {api, HydraData} from "../../api/apiProvider";
import {Button, Card, Col, Flex, Form, message, Row, Segmented, Space} from "antd";
import ButtonBlack from "../../components/ui/buttons/ButtonBlack";
import {NavLink, useParams} from "react-router-dom";
import Title from "antd/es/typography/Title";
import TextArea from "antd/lib/input/TextArea";
import useSWRMutation from "swr/mutation";
import Stages from "../../components/brief/Stages";
import {PlusOutlined} from "@ant-design/icons";
import ButtonWhite from "../../components/ui/buttons/ButtonWhite";
import {CSSTransition} from 'react-transition-group'
import IconLinkArrow from "../../components/ui/icons/IconLinkArrow";
import {useLayoutContext} from "./EditLayout";
import {Criterion} from "../../api/types";
import {cloneDeep} from "lodash";
import IconClose from "../../components/ui/icons/IconClose";
import Information from "../../components/information";
import IconComments from "../../components/ui/icons/IconComments";
import Roles from "../../roles/Roles";
import {isEqualValues} from "../../utils";
import Forbidden from "../results/forbidden";

interface CriterionItem {
  brief: string,
  text: string,
  //comment: string,
  id?: number,
  isAcceptedByClient?: string,
  isAcceptedInDevelopment?: string,
  commentInDevelopment?: string,
  commentToAcceptedByClient?: string,
  commentToResolvedByModeling?: string,
  isResolvedByModeling?: string,
  commentary?: string,
}

const getParamsString = () => {
  const params = new URLSearchParams([
    ['t[]', 'brief'], ['t[]', 'brief:stage'], ['t[]', 'product:company'],
    ['t[]', 'company'], ['t[]', 'company:pool'], ['t[]', 'pool']
  ]);

  return params.toString()
};

const updateCriterion = async (id: number, data: any) => {
  return await api.patch(`${urls.criterions}/${id}`, {arg: data})
};

const deleteCriterion = async (id: number) => {
  return await api.delete(`${urls.criterions}/${id}`)
};

export default function Tasks() {
  const {t} = useTranslation();
  const routeParams = useParams();
  const briefId = Number(routeParams.id) || 0;
  const [selectedCommentaryId, setSelectedCommentaryId] = useState<number>(0);
  const [criterionsForm, setCriterionsForm] = useState<CriterionItem[]>([]);
  const [form] = Form.useForm();
  const stagesNodeRef = useRef(null);
  const stagesButtonNodeRef = useRef(null);

  const [showStages, setShowStages] = useState(false);
  const [showStagesButton, setShowStagesButton] = useState(true);
  const [initialValues, setInitialValues] = useState<CriterionItem[]>();
  const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false);
  const {brief: layoutBrief} = useLayoutContext();

  const switchOptions = [
    {label: ' ', value: 'false', className: 'segmented-false',},
    {label: ' ', value: 'null', className: 'segmented-null',},
    {label: ' ', value: 'true', className: 'segmented-true',},
  ];

  const {
    data: brief,
    error: briefError,
    mutate: mutateBrief,
    isValidating: isBriefValidating,
  } = useSWR<HydraData>(briefId > 0 && briefId !== 0 ? `${urls.briefs}/${briefId}?${getParamsString()}` : null, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: criterions,
    error: criterionsError,
    mutate: mutateCriterions,
    isValidating: isCriterionsValidating,
  } = useSWR<HydraData>(briefId > 0 && briefId !== 0 ? `${urls.criterions}?brief.id=${briefId}&t[]=taskBD` : null, api.get, {
    revalidateOnFocus: false
  });

  const {trigger: createCriterion, isMutating: isBriefCreating}
    = useSWRMutation(urls.criterions, api.post, /* options */);

  useEffect(() => {
    if (criterions && criterions.data) {
      const data = criterions.data.map((item: CriterionItem) => ({
        brief: item.brief,
        text: item.text,
        id: item.id,
        isAcceptedByClient: String(item.isAcceptedByClient),
        isAcceptedInDevelopment: String(item.isAcceptedInDevelopment),
        commentInDevelopment: String(item.commentInDevelopment),
        commentToAcceptedByClient: String(item.commentToAcceptedByClient),
        commentToResolvedByModeling: String(item.commentToResolvedByModeling),
        isResolvedByModeling: String(item.isResolvedByModeling),
        commentaryId: item.commentary ? item.commentary.split('/').pop() : 0,
      }));

      setInitialValues(data);

      form.setFieldValue('criterions', data);

      setCriterionsForm(cloneDeep(data))
    }
  }, [criterions]);

  useEffect(() => {
    for (let criterion of criterionsForm) {
      if (criterion) {
        if (criterion.isAcceptedInDevelopment !== 'false') {
          criterion.commentInDevelopment = ''
        }

        if (criterion.isResolvedByModeling !== 'false') {
          criterion.commentToResolvedByModeling = ''
        }

        if (criterion.isAcceptedByClient !== 'false') {
          criterion.commentToAcceptedByClient = ''
        }
      }
    }
  }, [criterionsForm]);

  useEffect(() => {
    if (layoutBrief && brief && brief.data) {
      brief.data.isAccepted = layoutBrief.isAccepted
    }
  }, [layoutBrief, brief]);

  const getValueFromString = (value: string) => {
    if (value === 'null') {
      return null
    } else if (value === 'false') {
      return false
    } else if (value === 'true') {
      return true
    }

    return null
  };

  const onFinish = (values: any) => {
    if (values.criterions && values.criterions.length > 0) {
      Promise.all([...values.criterions.map((criterion: CriterionItem) => {
          const value: Criterion = {
            text: criterion.text,
            id: criterion.id,
            isAcceptedByClient: getValueFromString(criterion.isAcceptedByClient || ''),
            isAcceptedInDevelopment: getValueFromString(criterion.isAcceptedInDevelopment || ''),
            commentInDevelopment: criterion.commentInDevelopment,
            commentToAcceptedByClient: criterion.commentToAcceptedByClient,
            commentToResolvedByModeling: criterion.commentToResolvedByModeling,
            isResolvedByModeling: getValueFromString(criterion.isResolvedByModeling || ''),
          };

          if (!criterion.id) {
            value.brief = `/api/model/briefs/${briefId}`
          }

          return criterion.id ?
            updateCriterion(criterion.id, value) : createCriterion(value)
        }
      )
      ]).then(values => {
        message.success(t('common.saved'));
        mutateCriterions();
        setIsValuesChanged(false)
      }).catch(errors => {
        if (errors['hydra:description']) {
          message.error(errors['hydra:description'])
        }
        setIsValuesChanged(false)
      })
    }
  };

  const deleteItem = (id: number) => {
    if (!Roles.hasAccess('canCreateOrUpdateCriterion')) {
      return
    }

    deleteCriterion(id).then((response) => {
      message.success(t('common.deleted'));
      mutateCriterions()
    }).catch(errors => {
      if (errors['hydra:description']) {
        message.error(errors['hydra:description'])
      }
    })
  };

  const getCriterionItemClass = (value: string) => {
    return 'criterion-' + value
  };

  const onValuesChange = (allValues: any) => {
    if (allValues.length !== initialValues?.length) {
      setIsValuesChanged(true);
      return
    }

    for (let key in initialValues) {
      if (!initialValues.hasOwnProperty(key)) {
        continue
      }

      const initialValue = initialValues[Number(key)];
      if (key in allValues) {
        const changedValue = allValues[key];

        if (!isEqualValues(initialValue, changedValue)) {
          setIsValuesChanged(true);
          return
        }
      }
    }

    setIsValuesChanged(false)
  };

  return (
    <>
      {Roles.hasAccess('canReadCriterion') ?
        <Flex gap={'large'} style={{width: '100%'}} className={'page-wrapper tasks'}>
          <Space direction={'vertical'} size={'middle'} style={{width: '100%'}}>
            <Title level={5}>{t('edit.requirements')}</Title>
            <Form
              form={form}
              name="criterions"
              onFinish={onFinish}
              layout="vertical"
              autoComplete="off"
              onValuesChange={(changed, all) => {
                for (let index in all.criterions) {
                  let criterion = all.criterions[index]
                  if (criterion === undefined) {
                    all.criterions[index] = {
                      isAcceptedByClient: 'null',
                      isAcceptedInDevelopment: 'null',
                      isResolvedByModeling: 'null',
                    }
                  }
                }

                form.setFieldValue('criterions', all.criterions)
                setCriterionsForm(all.criterions)
                onValuesChange(all)
              }}
            >
              <Flex vertical gap={'large'}>
                <Form.List name="criterions">
                  {(fields, {add, remove}) => (
                    <Flex vertical gap={'middle'}>
                      {fields.map(({key, name, ...restField}) => (
                        <Card key={key}>
                          <Row gutter={8}>
                            <Col span={8}>
                              <Flex vertical gap={6}>
                                <Flex justify={'space-between'}>
                                  <span className={'criterion__title'}>{t('tasks.text')} №{name + 1}</span>
                                </Flex>
                                <Flex vertical>
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'text']}
                                    rules={[{required: true, message: t('tasks.taskEmpty')}]}
                                    style={{width: '100%'}}
                                  >
                                    <TextArea placeholder={t('common.write')} rows={5}
                                              disabled={!Roles.hasAccess('canCreateOrUpdateCriterion')}/>
                                  </Form.Item>
                                </Flex>
                              </Flex>
                            </Col>
                            <Col span={16}>
                              <Flex vertical gap={6}>
                                <Flex justify={'space-between'}>
                                  <span className={'criterion__title'}>{t('tasks.statuses')}</span>
                                  <Flex gap={'middle'} align={'center'}>
                                    {Roles.hasAccess('canCreateOrUpdateCriterion') &&
                                      <>
                                        <Button
                                          type={form.getFieldValue(["criterions", name, "commentaryId"]) === selectedCommentaryId
                                            ? 'default' : 'text'} onClick={() => {
                                          setSelectedCommentaryId(form.getFieldValue(["criterions", name, "commentaryId"]))
                                        }}>
                                          <Flex gap={'small'}>
                                            <IconComments/>{t('info.comments')}
                                          </Flex>
                                        </Button>

                                        <div className={'criterion__delete'} onClick={async () => {
                                          const id = form.getFieldValue(["criterions", name, "id"])

                                          if (id) {
                                            deleteItem(id)
                                          } else {
                                            remove(name)
                                          }
                                        }}>{t('common.delete')}</div>
                                      </>
                                    }
                                  </Flex>
                                </Flex>
                                <Row gutter={8}>
                                  <Col span={8}>
                                    <div className={'criterion__status-item'}>
                                <span
                                  className={'criterion__status-item__label'}>{t('tasks.isAcceptedInDevelopment')}</span>
                                      <Flex justify={'space-between'} align={'center'}>
                                        <span
                                          className={'criterion__status-item__text'}>{t('tasks.econometrician')}</span>
                                        <Flex align={'center'} gap={4}>
                                          {t('common.no')}
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'isAcceptedInDevelopment']}
                                            style={{width: '100%', margin: 0}}
                                          >
                                            <Segmented
                                              className={'segmented ' + (getCriterionItemClass(criterionsForm[name] && criterionsForm[name].isAcceptedInDevelopment || ''))}
                                              options={switchOptions}
                                              size={'small'}
                                              disabled={!Roles.hasAccess('canSetAcceptedInDevelopmentInCriterion')}
                                            />
                                          </Form.Item>
                                          {t('common.yes')}
                                        </Flex>
                                      </Flex>
                                      {criterionsForm && criterionsForm[name] && criterionsForm[name].isAcceptedInDevelopment === 'false' &&
                                        <Flex justify={'space-between'} align={'flex-start'} vertical>
                                          <span className={'criterion__status-item__text'}>{t('edit.comment')}</span>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'commentInDevelopment']}
                                            style={{width: '100%', margin: 0}}
                                          >
                                            <TextArea
                                              placeholder={t('tasks.describeProblem')}
                                              rows={1}
                                              disabled={!Roles.hasAccess('canSetAcceptedInDevelopmentInCriterion')}
                                            />
                                          </Form.Item>
                                        </Flex>
                                      }
                                    </div>
                                  </Col>
                                  <Col span={8}>
                                    <div className={'criterion__status-item'}>
                                <span
                                  className={'criterion__status-item__label'}>{t('tasks.isResolvedByModeling')}</span>
                                      <Flex justify={'space-between'} align={'center'}>
                                        <span
                                          className={'criterion__status-item__text'}>{t('tasks.econometrician')}</span>
                                        <Flex align={'center'} gap={4}>
                                          {t('common.no')}
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'isResolvedByModeling']}
                                            style={{width: '100%', margin: 0}}
                                          >
                                            <Segmented
                                              className={'segmented ' + (getCriterionItemClass(criterionsForm[name] && criterionsForm[name].isResolvedByModeling || ''))}
                                              options={switchOptions}
                                              size={'small'}
                                              disabled={!Roles.hasAccess('canSetResolvedByModelingInCriterion')}
                                            />
                                          </Form.Item>
                                          {t('common.yes')}
                                        </Flex>
                                      </Flex>
                                      {criterionsForm && criterionsForm[name] && criterionsForm[name].isResolvedByModeling === 'false' &&
                                        <Flex justify={'space-between'} align={'flex-start'} vertical>
                                          <span className={'criterion__status-item__text'}>{t('edit.comment')}</span>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'commentToResolvedByModeling']}
                                            style={{width: '100%', margin: 0}}
                                          >
                                            <TextArea
                                              placeholder={t('tasks.describeProblem')}
                                              rows={1}
                                              disabled={!Roles.hasAccess('canSetResolvedByModelingInCriterion')}
                                            />
                                          </Form.Item>
                                        </Flex>
                                      }
                                    </div>
                                  </Col>
                                  <Col span={8}>
                                    <div className={'criterion__status-item'}>
                                <span
                                  className={'criterion__status-item__label'}>{t('tasks.isAcceptedByClient')}</span>
                                      <Flex justify={'space-between'} align={'center'}>
                                        <span className={'criterion__status-item__text'}>{t('tasks.client')}</span>
                                        <Flex align={'center'} gap={4}>
                                          {t('common.no')}
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'isAcceptedByClient']}
                                            style={{width: '100%', margin: 0}}
                                          >
                                            <Segmented
                                              className={'segmented ' + (getCriterionItemClass(criterionsForm[name] && criterionsForm[name].isAcceptedByClient || ''))}
                                              options={switchOptions}
                                              size={'small'}
                                              disabled={!Roles.hasAccess('canSetAcceptedByClientInCriterion')}
                                            />
                                          </Form.Item>
                                          {t('common.yes')}
                                        </Flex>
                                      </Flex>
                                      {criterionsForm && criterionsForm[name] && criterionsForm[name].isAcceptedByClient === 'false' &&
                                        <Flex justify={'space-between'} align={'flex-start'} vertical>
                                          <span className={'criterion__status-item__text'}>{t('edit.comment')}</span>
                                          <Form.Item
                                            {...restField}
                                            name={[name, 'commentToAcceptedByClient']}
                                            style={{width: '100%', margin: 0}}
                                          >
                                            <TextArea
                                              placeholder={t('tasks.describeProblem')}
                                              rows={1}
                                              disabled={!Roles.hasAccess('canSetAcceptedByClientInCriterion')}
                                            />
                                          </Form.Item>
                                        </Flex>
                                      }
                                    </div>
                                  </Col>
                                </Row>
                              </Flex>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      {Roles.hasAccess('canCreateOrUpdateCriterion') &&
                        <Form.Item>
                          <Flex gap={'middle'}>
                            <ButtonWhite onClick={() => add()}>
                              <PlusOutlined/>
                              {t('tasks.addTask')}
                            </ButtonWhite>
                            <ButtonBlack
                              onClick={form.submit}
                              disabled={!isValuesChanged}
                            >{t('common.save')}</ButtonBlack>
                          </Flex>
                        </Form.Item>
                      }
                    </Flex>
                  )}
                </Form.List>
                {brief && brief.data && brief.data.isAccepted &&
                  <div className={'criterion__message'}>
                    <Flex align={'center'} justify={'center'} vertical gap={'large'}>
                      <span className={'criterion__message-title'}>{t('tasks.modelAccepted')}</span>
                      <NavLink className={'criterion__message-button'}
                               to={{pathname: `/briefs/${briefId}/documents`}}><Flex
                        gap={4} align={'center'}>{t('tasks.linkToDocuments')}
                        <IconLinkArrow/></Flex></NavLink>
                    </Flex>
                  </div>
                }
              </Flex>
            </Form>

          </Space>
          {selectedCommentaryId > 0 &&
            <section className={'side-section'}>
              <div className={'side-section__close'} onClick={() => setSelectedCommentaryId(0)}>
                <IconClose/>
              </div>
              <Information briefId={0} updateList={() => {
              }} commentaryId={selectedCommentaryId} showStages={false}/>
            </section>
          }

          <CSSTransition
            in={showStagesButton}
            nodeRef={stagesButtonNodeRef}
            timeout={400}
            classNames="criterion-stages-button"
            unmountOnExit
          >
            <div className={'criterion__stages-button'} onClick={() => {
              setShowStages(true)
              setShowStagesButton(false)
            }} ref={stagesButtonNodeRef}>
              <Flex justify={'space-between'} align={'center'}>
                <span className={'criterion__stages-button__text'}>{t('edit.stages')}</span>
                <span className={'criterion__stages-button__expand'}>{t('tasks.expand')}</span>
              </Flex>
            </div>

          </CSSTransition>

          <CSSTransition
            in={showStages}
            nodeRef={stagesNodeRef}
            timeout={400}
            classNames="criterion-stages"
            unmountOnExit
            onExiting={() => setShowStagesButton(true)}
          >
            <div className={'criterion__stages-block'} ref={stagesNodeRef}>
              <Flex justify={'space-between'} align={'center'} onClick={() => setShowStages(false)}>
                <span className={'criterion__stages-button__text'}>{t('edit.stages')}</span>
                <span className={'criterion__stages-button__expand'}>{t('tasks.collapse')}</span>
              </Flex>
              <Stages />
            </div>
          </CSSTransition>
        </Flex>
        : <Forbidden/>
      }
    </>
  )
}
